import React, { useState, useEffect } from 'react';
import {
  FiActivity, FiCalendar, FiTrendingUp, FiBook, FiTarget
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import nutritionService from '../../services/nutritionService';
import TodayNutrition from './widgets/TodayNutrition';
import MealPlanToday from './widgets/MealPlanToday';
import WeeklyProgress from './widgets/WeeklyProgress';
import QuickActions from './widgets/QuickActions';
import FoodLogger from '../FoodLogger/FoodLogger';
import ProgressDashboard from '../ProgressDashboard/ProgressDashboard';
import MacroCalculator from '../MacroCalculator/MacroCalculator';
import MealPlanViewer from './MealPlanViewer';
import './ClientNutritionDashboard.css';

const ClientNutritionDashboard = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [mealPlan, setMealPlan] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);
  const [allMealLogs, setAllMealLogs] = useState([]);
  const [progressEntries, setProgressEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: FiTarget },
    { id: 'logger', label: 'Food Logger', icon: FiActivity },
    { id: 'plan', label: 'My Meal Plan', icon: FiCalendar },
    { id: 'progress', label: 'Progress', icon: FiTrendingUp },
    { id: 'tools', label: 'Tools', icon: FiBook },
  ];

  useEffect(() => {
    if (currentUser) {
      fetchNutritionData();
    }
  }, [currentUser]);

  const fetchNutritionData = async () => {
    setLoading(true);
    try {
      // Fetch active nutrition plan
      const plansRef = collection(db, 'nutritionPlans');
      const plansQuery = query(
        plansRef,
        where('clientId', '==', currentUser.uid),
        where('status', '==', 'active'),
        firestoreLimit(1)
      );
      const plansSnapshot = await getDocs(plansQuery);

      if (!plansSnapshot.empty) {
        const planData = { id: plansSnapshot.docs[0].id, ...plansSnapshot.docs[0].data() };
        setNutritionPlan(planData);
      }

      // Fetch assigned meal plan
      const mealPlansRef = collection(db, 'mealPlans');
      const mealPlansQuery = query(
        mealPlansRef,
        where('clientId', '==', currentUser.uid),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(1)
      );
      const mealPlansSnapshot = await getDocs(mealPlansQuery);

      if (!mealPlansSnapshot.empty) {
        const mealPlanData = { id: mealPlansSnapshot.docs[0].id, ...mealPlansSnapshot.docs[0].data() };
        setMealPlan(mealPlanData);
      }

      // Fetch today's meal logs
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const logsRef = collection(db, 'mealLogs');
      const logsQuery = query(
        logsRef,
        where('userId', '==', currentUser.uid),
        where('timestamp', '>=', today),
        where('timestamp', '<', tomorrow),
        orderBy('timestamp', 'desc')
      );
      const logsSnapshot = await getDocs(logsQuery);
      const logs = logsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTodayLogs(logs);

      // Fetch all meal logs for the week (for compliance calculation)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const weekLogsQuery = query(
        logsRef,
        where('userId', '==', currentUser.uid),
        where('timestamp', '>=', weekAgo),
        orderBy('timestamp', 'desc')
      );
      const weekLogsSnapshot = await getDocs(weekLogsQuery);
      const weekLogs = weekLogsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllMealLogs(weekLogs);

      // Fetch progress entries
      const progressRef = collection(db, 'progressEntries');
      const progressQuery = query(
        progressRef,
        where('userId', '==', currentUser.uid),
        orderBy('date', 'desc'),
        firestoreLimit(30)
      );
      const progressSnapshot = await getDocs(progressQuery);
      const entries = progressSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProgressEntries(entries);

    } catch (error) {
      console.error('Error fetching nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAddMeal = () => {
    setActiveTab('logger');
  };

  const handleLogWeight = () => {
    setActiveTab('progress');
  };

  const handleViewMealPlan = () => {
    setActiveTab('plan');
  };

  const handleCalculateMacros = () => {
    setActiveTab('tools');
  };

  const handleLogMeal = (meal) => {
    // Auto-populate FoodLogger with meal plan items
    setActiveTab('logger');
    // TODO: Pass meal data to FoodLogger for quick logging
  };

  const renderDashboard = () => (
    <div className="nutrition-dashboard-view">
      <div className="dashboard-header">
        <h2>Nutrition Dashboard</h2>
        <p>Track your daily nutrition and reach your goals</p>
      </div>

      <div className="dashboard-grid">
        <div className="grid-item">
          <TodayNutrition
            logs={todayLogs}
            targets={nutritionPlan?.calculations?.macros}
            onQuickAdd={handleQuickAddMeal}
          />
        </div>

        <div className="grid-item">
          <MealPlanToday
            mealPlan={mealPlan}
            onLogMeal={handleLogMeal}
            onViewFullPlan={handleViewMealPlan}
          />
        </div>

        <div className="grid-item">
          <WeeklyProgress
            userId={currentUser.uid}
            mealLogs={allMealLogs}
            progressEntries={progressEntries}
          />
        </div>

        <div className="grid-item">
          <QuickActions
            onLogFood={handleQuickAddMeal}
            onLogWeight={handleLogWeight}
            onViewMealPlan={handleViewMealPlan}
            onCalculateMacros={handleCalculateMacros}
          />
        </div>
      </div>
    </div>
  );

  const renderFoodLogger = () => (
    <div className="tab-content-view">
      <FoodLogger
        targetMacros={nutritionPlan?.calculations?.macros}
        nutritionPlan={nutritionPlan}
      />
    </div>
  );

  const renderMealPlan = () => (
    <div className="tab-content-view">
      <MealPlanViewer
        mealPlan={mealPlan}
        onLogMeal={handleLogMeal}
      />
    </div>
  );

  const renderProgress = () => (
    <div className="tab-content-view">
      <ProgressDashboard
        userId={currentUser.uid}
        targetWeight={nutritionPlan?.bodyData?.weight}
      />
    </div>
  );

  const renderTools = () => (
    <div className="tab-content-view">
      <div className="tools-view">
        <div className="tools-header">
          <h2>Nutrition Tools</h2>
          <p>Calculate your macros and get nutrition guidance</p>
        </div>
        <MacroCalculator />
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'logger':
        return renderFoodLogger();
      case 'plan':
        return renderMealPlan();
      case 'progress':
        return renderProgress();
      case 'tools':
        return renderTools();
      default:
        return renderDashboard();
    }
  };

  if (loading) {
    return (
      <div className="client-nutrition-dashboard loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-nutrition-dashboard">
      {/* Tab Navigation */}
      <div className="nutrition-tabs">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="tab-content-container">
        {renderActiveTab()}
      </div>
    </div>
  );
};

export default ClientNutritionDashboard;
